import { Component, Host, Prop, h, Event, EventEmitter, State, Element } from '@stencil/core';

import appleSVG from './assets/apple.svg';
import facebookSVG from './assets/facebook.svg';
import googleSVG from './assets/google.svg';
import twitterSVG from './assets/twitter.svg';
import linkedInSVG from './assets/linked-in.svg';

/**
 * Represents the details for an authentication click event.
 */
export interface AuthClickEventDetail {
    idp?: string; // Identity provider name (e.g., 'google', 'facebook', etc.)
}

@Component({
    tag: 'login-wall',
    styleUrl: 'login-wall.css',
    shadow: true,
})
export class LoginWall {

    // Event names for external reference
    static readonly AUTH_EVENT = 'authBoxAuthClick';
    static readonly MORE_EVENT = 'authBoxMoreClick';

    @Element() el: HTMLElement;

    private visibleIdpsArray = []
    /**
     * Prop to specify which IDPs (Identity Providers) to show.
     * Accepts an strings like 'google,facebook,apple'.
     */
    @Prop() visibleIdps: string = 'google,apple,facebook,email';

    /**
     * The heading text displayed at the top of the login wall.
     */
    @Prop() heading: string = 'Melde dich kostenlos an und tauche tiefer in die Welt des Motorsports ein!';

    /**
     * The paragraph text displayed below the heading.
     */
    @Prop() paragraph: string = 'Um diesen Artikel weiterlesen zu können, musst du angemeldet sein.';

    /**
     * State to hold the available identity providers.
     */
    @State() idps: { id: string; name: string; logo: string }[] = [
        { id: 'google', name: 'Google', logo: googleSVG },
        { id: 'apple', name: 'Apple', logo: appleSVG },
        { id: 'facebook', name: 'Facebook', logo: facebookSVG },
        { id: 'instagram', name: 'Instagram', logo: facebookSVG },
        { id: 'twitter', name: 'X(Twitter)', logo: twitterSVG },
        { id: 'tiktok', name: 'TikTok', logo: facebookSVG },
        { id: 'linkedin', name: 'LinkedIn', logo: linkedInSVG },
    ];

    /**
     * Lifecycle hook
     */
    componentWillLoad(){
        this.visibleIdpsArray = this.visibleIdps.split(',');
    }

    /**
     * Event emitted when an identity provider or email login is selected.
     * 
     * - Detail: `{ idp: string }` where `idp` is the provider name (`'google'`, `'apple'`, `'facebook'`, or `'email'`).
     */
    @Event({ eventName: 'authBoxAuthClick' }) authClick: EventEmitter<AuthClickEventDetail>;

    /**
     * Event emitted when the more options button is clicked.
     */
    @Event({ eventName: 'authBoxMoreClick' }) moreOptions: EventEmitter<void>;

    /**
     * Handles clicks on an identity provider button and emits an `authClick` event with the selected provider.
     * 
     * @param idp - The ID of the selected identity provider.
     */
    private handleAuthClick(idp: string): void {
        this.authClick.emit({ idp })
    }

    /**
    * The handleMoreOptionsButton function toggles the options-active CSS class
    * for extending the social options
    */
    private handleMoreOptionsButton(): void {
        const authButtonsContainer = this.el.shadowRoot?.getElementById('authMoreButtonsContainerId');
        const moreOptionsButton = this.el.shadowRoot?.getElementById('moreOptionsButtonId');

        if (authButtonsContainer && moreOptionsButton) {
            authButtonsContainer.classList.toggle('options-active');
            moreOptionsButton.classList.toggle('options-active');

            // Check if the moreOptionsButton has the "options-active" class and switch the text
            if (moreOptionsButton.classList.contains('options-active')) {
                moreOptionsButton.textContent = 'weniger anzeigen';
                moreOptionsButton.ariaLabel = 'weniger anzeigen';
            } else {
                moreOptionsButton.textContent = 'mehr anzeigen';
                moreOptionsButton.ariaLabel = 'mehr anzeigen';
            }
        }
        this.moreOptions.emit()
    }

    /**
    * Renders the email authentication button if 'email' is included in `visibleIdps`.
    * 
    * @returns A JSX element containing the email authentication button, or undefined if not visible.
    */
    private renderEmailAuth() {
        if (this.visibleIdpsArray.includes('email')) {
            return (
                <div class="email-container">
                    <div class="auth-divider">oder</div>
                    <button class="email-auth" onClick={() => this.handleAuthClick('email')} aria-label={'E-mail'} title="E-Mail">
                        via E-Mail
                    </button>
                </div>
            )
        }
    }

    render() {
        // Filter the IDPs based on the `visibleIdps` Prop
        // Sort the `filteredIdps` array according to the order of `visibleIdps`
        const filteredIdps = this.idps
            .filter(idp => this.visibleIdpsArray.includes(idp.id))
            .sort((a, b) => this.visibleIdpsArray.indexOf(a.id) - this.visibleIdps.indexOf(b.id));
            
        // Split the providers into two groups: first 3 and the rest
        const firstThreeProviders = filteredIdps.slice(0, 3);
        const remainingProviders = filteredIdps.slice(3);

        return (
            <Host>
                <div class="login-wall-container">
                    <div class="wall-icon">
                        <svg width="25" height="27" viewBox="0 0 25 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g>
                                <path d="M1 19.7175V26.5001H24.174V19.7175C21.9131 18.587 16.374 16.3262 12.3044 16.3262C8.2348 16.3262 3.07247 18.587 1 19.7175Z" fill="currentColor" stroke="currentColor" stroke-width="0.5" stroke-linejoin="round" />
                                <circle cx="12.3042" cy="7.28263" r="6.78263" fill="currentColor" />
                            </g>
                        </svg>
                    </div>
                    <div class="content">
                        <div class="info">
                            <p class="title">{this.heading}</p>
                            <p class="subtitle">{this.paragraph}</p>
                        </div>

                        <slot></slot>

                        <div class="auth-box" >
                            <div class="social-buttons">
                                <div id='authButtonsContainerId' class="auth-buttons-container">
                                    {firstThreeProviders.map(idp => (
                                        <button class="auth-button" onClick={() => this.handleAuthClick(idp.id)} aria-label={idp.name} title={idp.name}>
                                            <img src={idp.logo} alt={`${idp.name}`} />
                                        </button>
                                    ))}
                                </div>

                                {remainingProviders.length > 0 && (
                                    <div class="more-socials">
                                        <div id='authMoreButtonsContainerId' class="auth-buttons-container">
                                            {remainingProviders.map(idp => (
                                                <button class="auth-button" onClick={() => this.handleAuthClick(idp.id)} aria-label={idp.name} title={idp.name}>
                                                    <img src={idp.logo} alt={`${idp.name}`} />
                                                </button>
                                            ))}
                                        </div>

                                        <button id="moreOptionsButtonId" class="more-options" onClick={() => this.handleMoreOptionsButton()}>mehr anzeigen</button>
                                    </div>
                                )}
                            </div>

                            {this.renderEmailAuth()}
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
